import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import {
  RiskIncidentSidePanelWebComponentSchema,
  Risk,
  EntityData,
  ROOT_CAUSE_RISKS_ICON,
  ROOT_CAUSE_TYPE,
  TYPES_OF_RISKS,
  RiskIncidentSidePanelEvents,
  TYPE_OF_ERA_RISKS, DeviceData,
} from '../types';
import { GzAccordionWebComponent } from '../../../ui/gzAccordion';
import { GzInputSearchWebComponent } from '../../../ui/gzInput';
import { ENTITIES_CLASS } from '../../../edr/incidentsGrid/types';
import { GzLabelSwitchWebComponent } from '../../../ui/gzLabelSwitch';
import { GzTooltipWebComponent } from '../../../ui/gzTooltip';
import { WEB_COMPONENT_INLINE_ROW_ACTIONS } from '../../../webcomponents.constants';
import { InlineRowActionsWebComponent } from '../../../grid/uiElements/inlineRowActions';
import { TemplateState } from '../../../types/common/types';
import { GzLoaderWebComponent } from '../../../ui/gzLoader';
import { ErrorCodes } from '../../riskIncidentPanel/types';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { GzTextWebComponent } from '../../../ui/gzText';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { htmlEncode } from '../../../utils/escape.utils';

export class RiskIncidentSidePanelView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;
    $inputSearch: GzInputSearchWebComponent;
    $titleHeader: HTMLDivElement;
    $accordions: HTMLElement;
    $licenseRequiredContainer: HTMLElement;
    $licenseRequiredTitle: GzTitleWebComponent;
    $licenseRequiredMessage: GzTextWebComponent;
    $licenseButton: GzButtonWebComponent;
  };

  public getDomElements(): void {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$inputSearch = this.shadow.querySelector('#risk-incident-side-panel-input-search');
    this.dom.$titleHeader = this.shadow.querySelector('.title-header');
    this.dom.$accordions = this.shadow.querySelector('#risk-incident-side-panel-accordions');
    this.dom.$licenseRequiredContainer = this.shadow.querySelector('#license-required');
    this.dom.$licenseRequiredTitle = this.shadow.querySelector('#license-required-title');
    this.dom.$licenseRequiredMessage = this.shadow.querySelector('#license-required-message');
    this.dom.$licenseButton = this.shadow.querySelector('#get-license');

    this.getTranslations();
    this.setTranslatedStrings();
  }

  public setAssociatedRisksPanelTitle(): void {
    const title = document.createElement('div');

    title.innerText = this.translations.panelTitle;
    title.className = 'side-panel-title';

    this.dom.$titleHeader.append(title);
  }

  private clearAssociatedRisksPanel(): void {
    this.dom.$accordions.innerHTML = '';
  }

  public setLoadingState(): void {
    this.setElementClass(this.dom.$inputSearch, 'hidden');
    this.resetTemplateState(this.dom.$accordions);
    this.setTemplateState(this.dom.$accordions, TemplateState.LOADING);
    this.clearAssociatedRisksPanel();
    this.setAssociatedRisksPanelTitle();

    const loadingTemplate = new GzLoaderWebComponent();
    loadingTemplate.rows = 15;
    loadingTemplate.columns = 2;

    this.dom.$accordions.appendChild(loadingTemplate);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$accordions);
  }

  public getTranslations(): object {
    this.translations = this.stringsTranslator.getRiskIncidentPanelStrings();
    return this.translations;
  }

  public setTranslatedStrings(): void {
    this.dom.$inputSearch.placeholder = this.dependencies.translateFn('Search risks');
    this.dom.$licenseRequiredTitle.innerText = this.translations.cloudSecurityLicenseRequired;
    this.dom.$licenseRequiredMessage.innerText = this.translations.reasonForCloudSecurityLicenseRequired;
    this.dom.$licenseButton.innerText = this.translations.getLicenseText;
  }

  public async buildView(result: RiskIncidentSidePanelWebComponentSchema): Promise<void> {
    this.removeElementClass(this.dom.$inputSearch, 'hidden');
    this.resetTemplateState(this.dom.$accordions);
    this.clearAssociatedRisksPanel();
    this.dom.$inputSearch.removeAttribute('visibility');
    const labelSwitch = new GzLabelSwitchWebComponent();
    const isERA = result.risksType === TYPES_OF_RISKS.TYPE_ERA;

    labelSwitch.itemLeft = {
      label: this.translations.endpointRisks,
      value: TYPES_OF_RISKS.TYPE_ERA,
      checked: isERA,
    };

    labelSwitch.itemRight = {
      label: this.translations.cloudRisks,
      value: TYPES_OF_RISKS.TYPE_CLOUD_SECURITY,
      checked: !isERA,
    };

    labelSwitch.id = 'risk-incident-side-panel-label-switch';
    this.dom.$titleHeader.append(labelSwitch);
    this.renderRiskCategoriesAccordions(result.data[result.risksType], result.risksType);
    await this.focusOnSpecificSection(result.sectionFocused);
  }

  public async focusOnSpecificSection(typeOfSectionFocused: string = ''): Promise<void> {
    const accordionElementName = '#risk-incident-side-panel-accordion-' + typeOfSectionFocused;
    const accordion = this.shadow.querySelector(accordionElementName);
    if (accordion) {
      await accordion.updateComplete;
      const accordionShadowRoot = accordion.shadowRoot;
      const accordionHeader = accordionShadowRoot.querySelector('.accordion-header');
      accordionHeader.setAttribute('tabindex', '-1');
      accordionHeader.focus({ preventScroll: false });
    }
  }

  public rerenderView(result: RiskIncidentSidePanelWebComponentSchema): void {
    const labelSwitch = this.shadow.querySelector('#risk-incident-side-panel-label-switch');

    this.changeLabelSwitchCheck(labelSwitch, result.risksType);
    this.renderRiskCategoriesAccordions(result.data[result.risksType], result.risksType);
  }

  public disableSwitch(isSwitchComponentDisabled: boolean): void {
    const labelSwitch = this.shadow.querySelector('#risk-incident-side-panel-label-switch');
    labelSwitch.disabled = isSwitchComponentDisabled;
  }

  private createRiskItemForMenuContent(risk, searchText): HTMLDivElement {
    const riskName = htmlEncode(risk.name);
    const highlightedText = this.getTextWithHighlight(riskName, searchText);

    const riskSideMenuContent = document.createElement('div');
    riskSideMenuContent.classList.add('risk-side-menu-content');

    const riskSideMenuLabeledContent = document.createElement('div');
    riskSideMenuLabeledContent.classList.add('risk-side-menu-labeled-content');

    const riskNameLabelTooltip = new GzTooltipWebComponent();
    riskNameLabelTooltip.classList.add('label-tooltip-risk-name');
    riskNameLabelTooltip.isEllipsis = true;
    riskNameLabelTooltip.tooltipText = riskName;
    const riskNameDiv = document.createElement('div');
    riskNameDiv.classList.add('risk-name');
    riskNameDiv.innerHTML = highlightedText;
    riskNameLabelTooltip.appendChild(riskNameDiv);
    riskSideMenuLabeledContent.appendChild(riskNameLabelTooltip);

    const riskNodeLabelTooltip = new GzTooltipWebComponent();
    riskNodeLabelTooltip.classList.add('label-tooltip-node-name');
    riskNodeLabelTooltip.isEllipsis = true;
    riskNodeLabelTooltip.tooltipText = risk.nodeName;
    const riskNodeName = document.createElement('div');
    riskNodeName.classList.add('risk-node-name');
    riskNodeName.textContent = risk.nodeName;
    riskNodeLabelTooltip.appendChild(riskNodeName);
    riskSideMenuLabeledContent.appendChild(riskNodeLabelTooltip);

    riskSideMenuContent.appendChild(riskSideMenuLabeledContent);
    return riskSideMenuContent;
  }

  private addMenuButtonForRisk(riskSideMenuContent: HTMLElement, riskType: string, risk: Risk, entity: string): void {
    if (!risk.specifics) {
      return;
    }

    const menuBtn = window.document.createElement(WEB_COMPONENT_INLINE_ROW_ACTIONS) as InlineRowActionsWebComponent;
    menuBtn.inlineActions = [];

    if (riskType === TYPES_OF_RISKS.TYPE_CLOUD_SECURITY) {
      this.createMenuButtonForCloudRisks(riskSideMenuContent, menuBtn, risk);
      return;
    }

    if (riskType === TYPES_OF_RISKS.TYPE_ERA || riskType === TYPES_OF_RISKS.TYPE_ROOT_CAUSE) {
      this.createMenuButtonForEraRisks(riskSideMenuContent, menuBtn, risk, entity);
      return;
    }
  }

  private createMenuButtonForCloudRisks(
    riskSideMenuContent: HTMLElement,
    menuBtn: InlineRowActionsWebComponent,
    risk: Risk
  ): void {
    if (risk.specifics?.ruleLink) {
      const value = { event: RiskIncidentSidePanelEvents.OPEN_CLOUD_SECURITY_LINK, data: risk.specifics.ruleLink };
      menuBtn.inlineActions.push({ title: this.translations.ruleLinkSidePanelText, value: JSON.stringify(value) });
    }

    if (risk.specifics?.ruleId) {
      const value = {
        event: RiskIncidentSidePanelEvents.PIVOT_TO_MISCONFIGURATION,
        data: {
          misconfigurationName: risk.name,
          misconfigurationId:
            Application.modules.riskManagement.controller.helpers.routinesTranslations.getRoutineIdByRuleId(
              risk.specifics.ruleId
            ),
        },
      };
      menuBtn.inlineActions.push({
        title: this.translations.ruleLinkRiskManagementSidePanelText,
        value: JSON.stringify(value),
      });
    }

    if (risk.specifics?.nodeLink) {
      const value = { event: RiskIncidentSidePanelEvents.OPEN_CLOUD_SECURITY_LINK, data: risk.specifics.nodeLink };
      menuBtn.inlineActions.push({ title: this.translations.nodeLinkSidePanelText, value: JSON.stringify(value) });
    }

    if (risk.specifics?.nodeName || risk.specifics?.nodeName === '') {
      const value = {
        event: RiskIncidentSidePanelEvents.PIVOT_TO_DEVICES_CLOUD,
        data: {
          deviceName: risk.nodeName,
          deviceId: risk.nodeId,
          deviceType: risk.specifics.type
        } as DeviceData
      };
      menuBtn.inlineActions.push({
        title: this.translations.nodeLinkRiskManagementSidePanelText,
        value: JSON.stringify(value),
        disabled: risk.specifics?.nodeName === '',
        tooltipText:
          risk.specifics?.nodeName === ''
            ? this.dependencies.translateFn(
                'No information is currently available for this resource in Risk Management.'
              )
            : undefined,
      });
    }

    menuBtn.state = null;
    if (
      risk.specifics.hasOwnProperty('errorCode') &&
      risk.specifics['errorCode'] === ErrorCodes.CSPM_LICENSE_NOT_FOUND
    ) {
      //Must be populated inlineActions to display the menu
      menuBtn.inlineActions = [
        {
          title: '',
          value: '',
        },
      ];
      const menuBtnTooltip = new GzTooltipWebComponent();
      menuBtnTooltip.classList.add('menu-button-tooltip');
      menuBtnTooltip.tooltipText = this.translations.cloudSecurityLicenseRequired;
      menuBtn.disabled = true;
      menuBtnTooltip.appendChild(menuBtn);
      riskSideMenuContent.appendChild(menuBtnTooltip);
      return;
    }

    riskSideMenuContent.appendChild(menuBtn);
  }

  private createMenuButtonForEraRisks(riskSideMenuContent: HTMLElement, menuBtn: InlineRowActionsWebComponent, risk: Risk, entity: string): void {
    const pivotToDevices = {
      event: RiskIncidentSidePanelEvents.PIVOT_TO_DEVICES,
      data: {
        deviceName: risk.nodeName,
        deviceId: risk.nodeId,
        deviceType: +entity,
      } as DeviceData,
    };
    menuBtn.inlineActions.push({
      title: this.translations.pivotToDeviceText,
      value: JSON.stringify(pivotToDevices),
      disabled: risk.specifics?.existsInEra === false,
      tooltipText:
        risk.specifics?.existsInEra === false
          ? this.dependencies.translateFn('No information is currently available for this resource in Risk Management.')
          : undefined,
    });

    if (risk.specifics.type === TYPE_OF_ERA_RISKS.ERA_RISK_TYPE_MISCONFIGURATION) {
      const pivotToMisconfigurations = {
        event: RiskIncidentSidePanelEvents.PIVOT_TO_MISCONFIGURATION,
        data: {
          misconfigurationName: risk.name,
          misconfigurationId: risk.routineId
        },
      };
      menuBtn.inlineActions.push({
        title: this.translations.pivotToMisconfigurationText,
        value: JSON.stringify(pivotToMisconfigurations),
      });
    }

    if (
      risk.specifics.type === TYPE_OF_ERA_RISKS.ERA_RISK_TYPE_APP_VULNERABILITIES ||
      risk.specifics.type === TYPE_OF_ERA_RISKS.ERA_RISK_TYPE_OS_VULNERABILITIES
    ) {
      const pivotToVulnerabilities = {
        event: RiskIncidentSidePanelEvents.PIVOT_TO_VULNERABILITIES,
        data: { vulnerabilityName: risk.name },
      };
      menuBtn.inlineActions.push({
        title: this.translations.pivotToVulnerabilityText,
        value: JSON.stringify(pivotToVulnerabilities),
      });
    }

    menuBtn.state = null;
    riskSideMenuContent.appendChild(menuBtn);
  }

  private renderRiskCategoriesAccordions(data: EntityData, riskType: string): void {
    const searchText = this.dom.$inputSearch.value;
    this.dom.$accordions.innerHTML = '';

    if (data.hasOwnProperty('errorCode') && data['errorCode'] === ErrorCodes.CSPM_LICENSE_NOT_FOUND) {
      this.setElementClass(this.dom.$inputSearch, 'hidden');
      this.removeElementClass(this.dom.$licenseRequiredContainer, 'hidden');
      return;
    }

    this.removeElementClass(this.dom.$inputSearch, 'hidden');
    this.setElementClass(this.dom.$licenseRequiredContainer, 'hidden');

    for (const entity in data) {
      if (data.hasOwnProperty(entity)) {
        this.createAccordion(entity, data, searchText, riskType);
      }
    }

    this.disableSwitch(false);
  }

  private createAccordion(entity: string, data: EntityData, searchText: string, riskType: string): void {
    const risks: Array<Risk> = data[entity]['items'];

    const gzAccordion: GzAccordionWebComponent = document.createElement('gz-accordion') as GzAccordionWebComponent;
    gzAccordion.setAttribute('align', 'left');

    if (risks.length === 0) {
      return;
    }

    const categoryName =
      entity === ROOT_CAUSE_TYPE ? this.translations.rootCauseRisks : Translator.translateXdrEntityType(entity);
    const gzAccordionRisks = document.createElement('div');
    risks.forEach(risk => {
      const riskSideMenuContent = this.createRiskItemForMenuContent(risk, searchText);
      this.addMenuButtonForRisk(riskSideMenuContent, riskType, risk, entity);
      gzAccordionRisks.appendChild(riskSideMenuContent);
    });
    let entityIconName;

    if (Number(entity) === 0) {
      entityIconName = ROOT_CAUSE_RISKS_ICON;
    } else {
      entityIconName = ENTITIES_CLASS.get(Number(entity));
    }

    gzAccordion.innerHTML += `
        <span slot="dynamic-title-content" class="accordion-slot d-inline-flex align-items-center">
          <gz-icon-label icon="${entityIconName}" iconColor="var(--chart-302-icon-buttons-titles)"></gz-icon-label>
          <gz-tooltip class="accordion-title" tooltiptext="${categoryName}" isellipsis="true">
            ${categoryName}
          </gz-tooltip>
          <gz-text class="accordion-count" fontWeight="300" fontSize="10">(${risks.length})</gz-text>
        </span>
      `;

    if (gzAccordionRisks) {
      gzAccordion.appendChild(gzAccordionRisks);
    }
    gzAccordion.setAttribute('removeContentPadding', '');
    gzAccordion.setAttribute('removeTitlePadding', '');
    gzAccordion.isOpen = true;
    gzAccordion.id = 'risk-incident-side-panel-accordion-' + entity;
    this.dom.$accordions.append(gzAccordion);
  }

  // returns the text that is bolded with matched searchText
  private getTextWithHighlight(text: string, searchText: string): string {
    if (searchText === '' || searchText.length < 2) {
      return text;
    }
    //remove special regex characters and replace spaces with | to create a regex or condition
    searchText = searchText
      .replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&')
      .replace(/^\s+/, '')
      .replace(/\s+/g, '|');

    return text.replace(new RegExp('(' + searchText + ')', 'ig'), '<b>$1</b>');
  }

  private changeLabelSwitchCheck(labelSwitch: GzLabelSwitchWebComponent, risksType: string): void {
    const isERA = risksType === TYPES_OF_RISKS.TYPE_ERA;

    labelSwitch.itemLeft.checked = isERA;
    labelSwitch.itemRight.checked = !isERA;
  }
}
